// components/keyboard/shortcutManager.ts
import { ShortcutConfig, ShortcutCategory } from './types';

class ShortcutManager {
  private shortcuts: Map<string, ShortcutConfig> = new Map();
  private isHelpOpen = false;
  private helpListeners: ((isOpen: boolean) => void)[] = [];

  private static instance: ShortcutManager;
  static getInstance(): ShortcutManager {
    if (!ShortcutManager.instance) {
      ShortcutManager.instance = new ShortcutManager();
    }
    return ShortcutManager.instance;
  }

  register(config: ShortcutConfig): void {
    this.shortcuts.set(config.id, config);
  }

  registerShortcut(config: ShortcutConfig): void {
    this.register(config);
  }

  unregister(id: string): void {
    this.shortcuts.delete(id);
  }

  unregisterShortcut(id: string): void {
    this.unregister(id);
  }

  triggerShortcut(id: string): void {
    const shortcut = this.shortcuts.get(id);
    if (shortcut?.enabled !== false) {
      shortcut?.callback();
    }
  }

  getShortcuts(): ShortcutConfig[] {
    return Array.from(this.shortcuts.values());
  }

  getShortcutsByCategory(): ShortcutCategory[] {
    const categories = new Map<string, ShortcutConfig[]>();
    
    this.shortcuts.forEach(shortcut => {
      if (!categories.has(shortcut.category)) {
        categories.set(shortcut.category, []);
      }
      categories.get(shortcut.category)!.push(shortcut);
    });

    return Array.from(categories.entries()).map(([name, shortcuts]) => ({
      name,
      description: this.getCategoryDescription(name),
      shortcuts: shortcuts.sort((a, b) => a.name.localeCompare(b.name))
    }));
  }

  showHelp(): void {
    this.isHelpOpen = true;
    this.notifyHelpListeners();
  }

  hideHelp(): void {
    this.isHelpOpen = false;
    this.notifyHelpListeners();
  }

  getHelpState(): boolean {
    return this.isHelpOpen;
  }

  onHelpStateChanged(callback: (isOpen: boolean) => void): void {
    this.helpListeners.push(callback);
  }

  private notifyHelpListeners(): void {
    this.helpListeners.forEach(callback => callback(this.isHelpOpen));
  }

  private getCategoryDescription(category: string): string {
    const descriptions: Record<string, string> = {
      'Navigation': 'Quickly navigate between different sections',
      'Configuration': 'Manage application settings',
      'Quotations': 'Handle invoices and quotations',
      'Reports': 'Generate and view reports',
      'Actions': 'Perform common actions',
      'General': 'General application shortcuts',
      'Tasks': 'Task management shortcuts',
    };
    return descriptions[category] || 'Application shortcuts';
  }

  clearAll(): void {
    this.shortcuts.clear();
  }

  getShortcut(id: string): ShortcutConfig | undefined {
    return this.shortcuts.get(id);
  }

  hasShortcut(id: string): boolean {
    return this.shortcuts.has(id);
  }
}

export const shortcutManager = ShortcutManager.getInstance();